import { useState, useCallback } from "react";
import {
  Image as ImageIcon,
  SlidersHorizontal,
  Palette,
  Megaphone,
} from "lucide-react";
import { cn } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { HeroSlidesTab } from "@/components/admin/content/HeroSlidesTab";
import { BrandsTab } from "@/components/admin/content/BrandsTab";
import { PromotionsTab } from "@/components/admin/content/PromotionsTab";

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

type TabKey = "hero" | "brands" | "promotions";

interface TabDef {
  readonly key: TabKey;
  readonly label: string;
  readonly icon: typeof SlidersHorizontal;
}

const TABS: readonly TabDef[] = [
  { key: "hero", label: "Hero Slides", icon: SlidersHorizontal },
  { key: "brands", label: "Marques", icon: Palette },
  { key: "promotions", label: "Promotions", icon: Megaphone },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ContentManager() {
  const [activeTab, setActiveTab] = useState<TabKey>("hero");

  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <ImageIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Gestion du contenu
            </h1>
            <p className="text-sm text-stone-500">
              Gérez les slides, marques et promotions du site.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-stone-200">
          <nav className="-mb-px flex gap-6" aria-label="Onglets">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={cn(
                    "flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                    isActive
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-stone-500 hover:border-stone-300 hover:text-stone-700",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === "hero" && <HeroSlidesTab />}
        {activeTab === "brands" && <BrandsTab />}
        {activeTab === "promotions" && <PromotionsTab />}
      </div>
    </AdminLayout>
  );
}
